import { useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import CardContainer from "../../../../components/Card/CardContainer";
import ModalBuyerPersona from "./ModalBuyerPersona";
import SectionHeader from "./SectionHeader";
import GroupsIcon from "@mui/icons-material/Groups";
import {
  SECTION_BUYER_PERSONA_TITLE,
  SECTION_BUYER_PERSONA_SUBTITLE,
  SECTION_BUYER_PERSONA_LABEL,
} from "../../../../i18n/keysTranslations";
import { useTranslationApp } from "../../../../lib/i18next";
import { BORDER_RADIUS } from "../../../../utils/constants";
import {
  BORDER_GRAY_COLOR,
  INPUT_COLOR_BORDER,
} from "../../../../utils/colors";

const getPersonaDisplayName = (persona, fallbackLabel) => {
  return persona?.name?.trim?.() ? persona.name : fallbackLabel;
};

const BuyerPersonaCard = ({ persona, index, nameLabel, onEdit, onDelete }) => {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        borderRadius: BORDER_RADIUS,
        border: `1px solid ${BORDER_GRAY_COLOR}`,
        borderColor: INPUT_COLOR_BORDER,
        bgcolor: "background.paper",
        px: 2,
        py: 2,
        cursor: "pointer",
      }}
      onClick={() => onEdit(index)}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              fontSize: "16px",
              fontWeight: 500,
            }}
          >
            {nameLabel}
          </Typography>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              flexShrink: 0,
            }}
          >
            <IconButton
              size="small"
              onClick={(event) => {
                event.stopPropagation();
                onEdit(index);
              }}
              aria-label={`Editar ${nameLabel}`}
              sx={{
                p: 0.5,
                color: "text.secondary",
                "&:hover": {
                  bgcolor: "action.hover",
                },
              }}
            >
              <EditIcon sx={{ fontSize: 17 }} />
            </IconButton>
            <IconButton
              size="small"
              onClick={(event) => {
                event.stopPropagation();
                onDelete(index);
              }}
              aria-label={`Eliminar ${nameLabel}`}
              sx={{
                p: 0.5,
                color: "text.secondary",
                "&:hover": {
                  bgcolor: "action.hover",
                },
              }}
            >
              <CloseIcon sx={{ fontSize: 17 }} />
            </IconButton>
          </Box>
        </Box>

        <Typography
          variant="body2"
          sx={{
            mt: 0.5,
            color: "text.secondary",
            fontSize: "14px",
            overflow: "hidden",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
          }}
        >
          {persona.text}
        </Typography>
      </Box>
    </Box>
  );
};

const BuyerPersonaCards = ({
  buyerPersonas,
  getNameLabel,
  onEdit,
  onDelete,
}) => {
  if (!Array.isArray(buyerPersonas) || buyerPersonas.length === 0) {
    return null;
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {buyerPersonas.map((persona, index) => {
        const nameLabel = getNameLabel(persona);
        return (
          // eslint-disable-next-line react/no-array-index-key
          <BuyerPersonaCard
            key={index}
            persona={persona}
            index={index}
            nameLabel={nameLabel}
            onEdit={onEdit}
            onDelete={onDelete}
          />
        );
      })}
    </Box>
  );
};

export const BuyerPersonaSection = ({ formMethods, hasError = false }) => {
  const { t } = useTranslationApp();
  const [selectedPersonaIndex, setSelectedPersonaIndex] = useState(null);
  const [localPersona, setLocalPersona] = useState({ name: "", text: "" });
  const [isModalOpen, setIsModalOpen] = useState(false);

  const businessBuyerPerson = formMethods?.watch?.("businessBuyerPerson") || [];

  if (!formMethods || !formMethods.control) {
    return null;
  }

  const { setValue } = formMethods;

  const buyerPersonaCount = Array.isArray(businessBuyerPerson)
    ? businessBuyerPerson.length
    : 0;
  const isAddDisabled = buyerPersonaCount >= 3;

  const handleOpenModal = (index) => {
    const persona = businessBuyerPerson[index] || { name: "", text: "" };
    setSelectedPersonaIndex(index);
    setLocalPersona({
      name: persona.name || "",
      text: persona.text || "",
    });
    setIsModalOpen(true);
  };

  const handleCreatePersona = () => {
    if (!Array.isArray(businessBuyerPerson)) {
      setSelectedPersonaIndex(0);
      setLocalPersona({ name: "", text: "" });
      setIsModalOpen(true);
      return;
    }

    if (businessBuyerPerson.length >= 3) {
      return;
    }

    const newIndex = businessBuyerPerson.length;
    setSelectedPersonaIndex(newIndex);
    setLocalPersona({ name: "", text: "" });
    setIsModalOpen(true);
  };

  const handleDeletePersona = (index) => {
    if (!Array.isArray(businessBuyerPerson)) return;
    if (index < 0 || index >= businessBuyerPerson.length) return;

    const updatedList = [...businessBuyerPerson];
    updatedList.splice(index, 1);
    setValue("businessBuyerPerson", updatedList, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedPersonaIndex(null);
    setLocalPersona({ name: "", text: "" });
  };

  const handleSavePersona = ({ name: newName, text: newText } = {}) => {
    if (selectedPersonaIndex === null || !Array.isArray(businessBuyerPerson)) {
      handleCloseModal();
      return;
    }

    const updatedList = [...businessBuyerPerson];
    updatedList[selectedPersonaIndex] = {
      name: newName || "",
      text: newText || "",
    };

    setValue("businessBuyerPerson", updatedList, {
      shouldDirty: true,
      shouldValidate: true,
    });
    handleCloseModal();
  };

  return (
    <>
      <CardContainer title="" hasError={hasError}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            minHeight: "100%",
          }}
        >
          {/* Header */}
          <SectionHeader
            icon={GroupsIcon}
            title={SECTION_BUYER_PERSONA_TITLE}
            subtitle={SECTION_BUYER_PERSONA_SUBTITLE}
            actionButton
            actionButtonDisabled={isAddDisabled}
            onActionButtonClick={handleCreatePersona}
            actionButtonIcon={AddIcon}
          />

          {/* Content */}
          <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
            <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
              <BuyerPersonaCards
                buyerPersonas={businessBuyerPerson}
                getNameLabel={(persona) =>
                  getPersonaDisplayName(persona, t(SECTION_BUYER_PERSONA_LABEL))
                }
                onEdit={handleOpenModal}
                onDelete={handleDeletePersona}
              />
            </Box>
          </Box>
        </Box>
      </CardContainer>

      <ModalBuyerPersona
        modalOpen={isModalOpen}
        onCloseModal={handleCloseModal}
        name={localPersona.name}
        text={localPersona.text}
        onSave={handleSavePersona}
      />
    </>
  );
};

export default BuyerPersonaSection;
